import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  modalityTerms,
  type AdditionalCoverage,
  type AdditionalCoverageCalculationType,
  type AdditionalCoverageInput,
  type AdditionalCoverageInsuredAmountBasis,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatDate,
  formatMoney,
  formatRate,
  moneyInputError,
  moneyToInput,
  rateInputError,
  rateToBasisPoints,
  rateToInput,
  toMoney,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { DetailCard, DetailTabs } from '@/shared/ui/DetailLayout';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';
import { Marcacao } from './CondicoesParticulares';
import { TextoDaModalidade } from './TextoDaModalidade';
import { campoRecusado, formasDaImportancia, tiposDeCalculo } from './textos';

function rotuloDoTipo(valor: string | null): string {
  return tiposDeCalculo.find((t) => t.value === (valor ?? ''))?.label ?? valor ?? '';
}

/** O "Valor do Calculo da Cobertura": a taxa do tipo escolhido. */
function valorDoCalculo(c: AdditionalCoverage): string {
  if (c.calculation_type === 'agravamento_premio' && c.aggravation_rate_bps !== null) {
    return formatRate(c.aggravation_rate_bps);
  }
  if (
    c.calculation_type === 'distribuicao_premio' &&
    c.distribution_rate_bps !== null
  ) {
    return formatRate(c.distribution_rate_bps);
  }
  return '-';
}

function taxaParaCampo(valor: number | null): string {
  return valor === null ? '' : rateToInput(valor);
}

function campoParaTaxa(texto: string): number | null {
  return texto.trim() === '' ? null : rateToBasisPoints(texto);
}

function erroDeTaxa(texto: string): string | undefined {
  return texto.trim() === '' ? undefined : rateInputError(texto);
}

function FormularioDaCobertura({
  modalityId,
  cobertura,
  onFechar,
  onSalva,
}: {
  modalityId: string;
  cobertura: AdditionalCoverage | null;
  onFechar: () => void;
  onSalva: () => void;
}) {
  const [nome, setNome] = useState(cobertura?.name ?? '');
  const [codigo, setCodigo] = useState(cobertura?.insurer_reference_code ?? '');
  const [tipo, setTipo] = useState<string>(cobertura?.calculation_type ?? '');
  const [agravo, setAgravo] = useState(
    taxaParaCampo(cobertura?.aggravation_rate_bps ?? null),
  );
  const [distribuicao, setDistribuicao] = useState(
    taxaParaCampo(cobertura?.distribution_rate_bps ?? null),
  );
  const [forma, setForma] = useState<string>(cobertura?.insured_amount_basis ?? '');
  const [valorFixo, setValorFixo] = useState(
    cobertura?.insured_amount ? moneyToInput(cobertura.insured_amount) : '',
  );
  const [percentual, setPercentual] = useState(
    taxaParaCampo(cobertura?.insured_amount_bps ?? null),
  );
  const [adicional, setAdicional] = useState(cobertura?.is_additional ?? true);
  const [automatica, setAutomatica] = useState(
    cobertura?.auto_issuance_enabled ?? false,
  );
  const [manual, setManual] = useState(cobertura?.allow_manual_edit ?? false);

  const erros = {
    agravo: erroDeTaxa(agravo),
    distribuicao: erroDeTaxa(distribuicao),
    percentual: erroDeTaxa(percentual),
    valorFixo: valorFixo.trim() === '' ? undefined : moneyInputError(valorFixo),
  };
  const temErro = Object.values(erros).some(Boolean);

  const salvar = useMutation({
    mutationFn: () => {
      const corpo: AdditionalCoverageInput = {
        name: nome.trim(),
        insurer_reference_code: codigo.trim(),
        calculation_type: (tipo || null) as AdditionalCoverageCalculationType | null,
        aggravation_rate_bps: campoParaTaxa(agravo),
        distribution_rate_bps: campoParaTaxa(distribuicao),
        insured_amount_basis: (forma ||
          null) as AdditionalCoverageInsuredAmountBasis | null,
        insured_amount: valorFixo.trim() === '' ? null : toMoney(valorFixo),
        insured_amount_bps: campoParaTaxa(percentual),
        is_additional: adicional,
        auto_issuance_enabled: automatica,
        allow_manual_edit: manual,
      };
      return cobertura
        ? modalityTerms.updateCoverage(modalityId, cobertura.id, corpo)
        : modalityTerms.createCoverage(modalityId, corpo);
    },
    onSuccess: onSalva,
  });
  const apiError = salvar.error instanceof ApiError ? salvar.error : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!temErro) salvar.mutate();
  }

  return (
    <Modal
      open
      size="lg"
      onClose={onFechar}
      title={cobertura ? `Editar cobertura: ${cobertura.name}` : 'Cadastrar cobertura'}
    >
      <form className="flex flex-col gap-4" onSubmit={enviar} noValidate>
        <Alert tone="warning" title="Calculo desligado">
          O tipo e o valor do calculo e a forma da importancia segurada ficam guardados
          como parametro. Nenhum calculo os le ate a homologacao (MOD-005, MOD-006 e
          PRC-004): na cotacao, o valor da cobertura continua digitado por quem tem a
          alcada.
        </Alert>
        {salvar.error && !campoRecusado(apiError) ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {salvar.error.message}
          </Alert>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Cobertura"
            required
            value={nome}
            maxLength={200}
            error={fieldErrorOf(apiError, 'name')}
            onChange={(e) => setNome(e.target.value)}
          />
          <Field
            label="Codigo de referencia"
            value={codigo}
            maxLength={40}
            error={fieldErrorOf(apiError, 'insurer_reference_code')}
            onChange={(e) => setCodigo(e.target.value)}
          />
          <Select
            label="Tipo de Calculo da Cobertura"
            options={tiposDeCalculo}
            value={tipo}
            error={fieldErrorOf(apiError, 'calculation_type')}
            onChange={(e) => setTipo(e.target.value)}
          />
          {tipo === 'distribuicao_premio' ? (
            <Field
              label="Taxa de distribuicao (%)"
              inputMode="decimal"
              value={distribuicao}
              error={
                erros.distribuicao ?? fieldErrorOf(apiError, 'distribution_rate_bps')
              }
              onChange={(e) => setDistribuicao(e.target.value)}
            />
          ) : (
            <Field
              label="Taxa de agravamento (%)"
              inputMode="decimal"
              value={agravo}
              error={erros.agravo ?? fieldErrorOf(apiError, 'aggravation_rate_bps')}
              onChange={(e) => setAgravo(e.target.value)}
            />
          )}
          <Select
            label="Forma da importancia segurada"
            options={formasDaImportancia}
            value={forma}
            error={fieldErrorOf(apiError, 'insured_amount_basis')}
            onChange={(e) => setForma(e.target.value)}
          />
          {forma === 'percentual_basica' ? (
            <Field
              label="Percentual da basica (%)"
              inputMode="decimal"
              value={percentual}
              error={erros.percentual ?? fieldErrorOf(apiError, 'insured_amount_bps')}
              onChange={(e) => setPercentual(e.target.value)}
            />
          ) : (
            <MoneyField
              label="Importancia segurada fixa"
              value={valorFixo}
              disabled={forma !== 'valor_fixo'}
              error={erros.valorFixo ?? fieldErrorOf(apiError, 'insured_amount')}
              onValueChange={setValorFixo}
            />
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Marcacao
            label="Cobertura adicional"
            checked={adicional}
            onChange={setAdicional}
            disabled={false}
          />
          <Marcacao
            label="Cobertura habilitada para emissao automatica"
            checked={automatica}
            onChange={setAutomatica}
            disabled={false}
          />
          <Marcacao
            label="Permite edicao manual na cotacao"
            checked={manual}
            onChange={setManual}
            disabled={false}
          />
        </div>
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onFechar}>
            Fechar
          </Button>
          <Button type="submit" loading={salvar.isPending} disabled={temErro}>
            Salvar cobertura
          </Button>
        </div>
      </form>
    </Modal>
  );
}

type AbaDaCobertura = 'objeto' | 'impressao' | 'condicoes';

const abasDaCobertura: { key: AbaDaCobertura; title: string }[] = [
  { key: 'objeto', title: 'Objeto da cobertura' },
  { key: 'impressao', title: 'Texto impresso na apolice' },
  { key: 'condicoes', title: 'Condicoes especiais da cobertura' },
];

/**
 * Os textos proprios da cobertura: o objeto da cotacao, o texto impresso na apolice
 * (que no OnPoint e outro, D24 do 46) e as condicoes especiais.
 */
function TextosDaCobertura({
  modalityId,
  cobertura,
}: {
  modalityId: string;
  cobertura: AdditionalCoverage;
}) {
  const [aba, setAba] = useState<AbaDaCobertura>('objeto');
  return (
    <DetailCard
      title={`Textos da cobertura: ${cobertura.name}`}
      description="Cada texto e versionado como o objeto da modalidade: salvar grava o rascunho, e outra pessoa o poe em vigor."
    >
      <DetailTabs
        tabs={abasDaCobertura}
        active={aba}
        onChange={setAba}
        label="Textos da cobertura"
      />
      {aba === 'objeto' ? (
        <TextoDaModalidade
          key="objeto"
          referencia={{
            family: 'objeto',
            modalityId,
            coverageId: cobertura.id,
            purpose: 'cotacao',
          }}
          rotuloDoEditor="Objeto da cobertura"
          ajudaDoEditor="Texto do objeto da cobertura adicional na cotacao, com as TAGs. Saneado de novo no servidor ao salvar."
          rotuloDeSalvar="Salvar objeto da cobertura"
          comTags
        />
      ) : aba === 'impressao' ? (
        <TextoDaModalidade
          key="impressao"
          referencia={{
            family: 'objeto',
            modalityId,
            coverageId: cobertura.id,
            purpose: 'impressao_apolice',
          }}
          rotuloDoEditor="Texto impresso na apolice"
          ajudaDoEditor="O texto impresso da cobertura nao e o da cotacao (D24 do 46). Vazio, a apolice usa o objeto da cotacao; a escolha e a decisao 5 da 11.12."
          rotuloDeSalvar="Salvar texto impresso"
          comTags
        />
      ) : (
        <TextoDaModalidade
          key="condicoes"
          referencia={{
            family: 'condicao',
            modalityId,
            coverageId: cobertura.id,
            kind: 'condicoes_especiais',
          }}
          rotuloDoEditor="Condicoes especiais da cobertura"
          ajudaDoEditor="Condicoes especiais proprias da cobertura adicional (48, MIN-118). Saneadas de novo no servidor ao salvar."
          rotuloDeSalvar="Salvar condicoes especiais da cobertura"
        />
      )}
    </DetailCard>
  );
}

/**
 * Secao "Coberturas Adicionais": Cobertura, Data de cadastro, Codigo de referencia,
 * Tipo de Calculo da Cobertura, Valor do Calculo da Cobertura, Status e as acoes.
 */
export function CoberturasAdicionais({ modalityId }: { modalityId: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeCriar = can('parametrizacao.modalidade.create');
  const podeEditar = can('parametrizacao.modalidade.update');
  const [editando, setEditando] = useState<AdditionalCoverage | 'nova' | null>(null);
  const [textos, setTextos] = useState<AdditionalCoverage | null>(null);
  const chave = ['additional-coverages', modalityId];

  const consulta = useQuery({
    queryKey: chave,
    queryFn: ({ signal }) =>
      modalityTerms.listCoverages(modalityId, { include_inactive: true }, signal),
  });
  const situacao = useMutation({
    mutationFn: (c: AdditionalCoverage) =>
      modalityTerms.updateCoverage(modalityId, c.id, {
        status: c.status === 'ativo' ? 'inativo' : 'ativo',
      }),
    onSuccess: (c) => {
      toast.success(
        c.status === 'ativo' ? 'Cobertura ativada.' : 'Cobertura inativada.',
      );
      void queryClient.invalidateQueries({ queryKey: chave });
    },
    onError: (erro) => toast.error(erro.message),
  });

  return (
    <div className="flex flex-col gap-5">
      <DetailCard
        title="Coberturas Adicionais"
        description="As coberturas adicionais da modalidade, como Multa e Trabalhista e Previdenciario. O calculo de cada uma fica guardado e desligado ate a homologacao."
        action={
          podeCriar ? (
            <Button size="sm" onClick={() => setEditando('nova')}>
              Cadastrar cobertura
            </Button>
          ) : undefined
        }
        loading={consulta.isPending}
        error={consulta.error}
      >
        <DataTable
          columns={[
            {
              key: 'cobertura',
              header: 'Cobertura',
              render: (c: AdditionalCoverage) => (
                <span className="font-medium text-ink-900">{c.name}</span>
              ),
            },
            {
              key: 'cadastro',
              header: 'Data de cadastro',
              render: (c: AdditionalCoverage) => formatDate(c.created_at),
            },
            {
              key: 'codigo',
              header: 'Codigo de referencia',
              render: (c: AdditionalCoverage) => c.insurer_reference_code ?? '-',
            },
            {
              key: 'tipo',
              header: 'Tipo de Calculo da Cobertura',
              render: (c: AdditionalCoverage) =>
                rotuloDoTipo(c.calculation_type) || '-',
            },
            {
              key: 'valor',
              header: 'Valor do Calculo da Cobertura',
              render: (c: AdditionalCoverage) => (
                <span>
                  {valorDoCalculo(c)}
                  {c.insured_amount ? (
                    <span className="block text-2xs text-ink-500">
                      IS fixa {formatMoney(c.insured_amount)}
                    </span>
                  ) : null}
                </span>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              render: (c: AdditionalCoverage) => (
                <StatusBadge kind="cadastro" value={c.status} />
              ),
            },
            {
              key: 'acoes',
              header: '',
              render: (c: AdditionalCoverage) => (
                <div className="flex flex-wrap gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setTextos(c)}>
                    Textos
                  </Button>
                  {podeEditar ? (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setEditando(c)}>
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={situacao.isPending && situacao.variables?.id === c.id}
                        onClick={() => situacao.mutate(c)}
                      >
                        {c.status === 'ativo' ? 'Inativar' : 'Ativar'}
                      </Button>
                    </>
                  ) : null}
                </div>
              ),
            },
          ]}
          items={consulta.data ?? []}
          keyOf={(c) => c.id}
          empty={<EmptyState icon="layers" title="Nenhuma cobertura adicional ainda" />}
        />
      </DetailCard>

      {textos ? (
        <TextosDaCobertura key={textos.id} modalityId={modalityId} cobertura={textos} />
      ) : null}

      {editando ? (
        <FormularioDaCobertura
          modalityId={modalityId}
          cobertura={editando === 'nova' ? null : editando}
          onFechar={() => setEditando(null)}
          onSalva={() => {
            setEditando(null);
            toast.success('Cobertura salva.');
            void queryClient.invalidateQueries({ queryKey: chave });
          }}
        />
      ) : null}
    </div>
  );
}
